/**
 * Row-reading helpers shared by the Call Report analytics panels.
 *
 * Report rows arrive as untyped API objects, and numbers can land as `number`,
 * as a numeric string, or as null. Every panel would otherwise repeat the same
 * three-branch coercion inline, and the one place that forgets it silently
 * produces `NaN` in a KPI tile — which renders as a plausible-looking dash
 * rather than as an error.
 */

/** A row value as trimmed text; null/undefined become ''. */
export function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim()
}

/** A row value as a finite number, or null. Never NaN. */
export function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/** A row value as a number for summing — absent counts as zero. */
export function numOr0(value: unknown): number {
  return num(value) ?? 0
}

/** Sum one column across rows. */
export function sumCol(rows: readonly Record<string, unknown>[], key: string): number {
  let total = 0
  for (const row of rows) total += numOr0(row[key])
  return total
}

/**
 * The mean of a column, ignoring rows where it is absent.
 *
 * Absent is NOT zero: averaging a null talk time as 0 drags the average down
 * by every unanswered call, which is precisely the call that has no talk time
 * to average.
 */
export function avgCol(
  rows: readonly Record<string, unknown>[],
  key: string,
): number | null {
  let total = 0
  let count = 0
  for (const row of rows) {
    const v = num(row[key])
    if (v === null) continue
    total += v
    count += 1
  }
  return count === 0 ? null : total / count
}

/**
 * A weighted mean — an average of averages, weighted by the rows behind each.
 *
 * The interval reports carry per-bucket averages, and a plain mean of those
 * treats a bucket with three calls as equal to one with three hundred. The
 * report's headline "average handle time" must be the average over CALLS.
 */
export function weightedAvgCol(
  rows: readonly Record<string, unknown>[],
  valueKey: string,
  weightKey: string,
): number | null {
  let weighted = 0
  let weight = 0
  for (const row of rows) {
    const v = num(row[valueKey])
    const w = numOr0(row[weightKey])
    if (v === null || w <= 0) continue
    weighted += v * w
    weight += w
  }
  return weight === 0 ? null : weighted / weight
}

/** Group rows by a text column and sum a numeric one, high→low. */
export function sumBoardBy(
  rows: readonly Record<string, unknown>[],
  labelKey: string,
  valueKey: string,
  options: { limit?: number; emptyLabel?: string } = {},
): { label: string; value: number }[] {
  const limit = options.limit ?? 8
  const emptyLabel = options.emptyLabel ?? 'Unspecified'
  const totals = new Map<string, number>()
  for (const row of rows) {
    const label = text(row[labelKey]) || emptyLabel
    totals.set(label, (totals.get(label) ?? 0) + numOr0(row[valueKey]))
  }
  return [...totals.entries()]
    .map(([label, value]) => ({ label, value }))
    .filter((e) => e.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, limit)
}

/**
 * A trend over the report's own interval buckets, summing a column per bucket.
 *
 * Drawn on `bucket`/`bucketStart` — the interval the operator chose in the
 * drawer — rather than on a bucketing invented in the chart, so the trend and
 * the table cannot disagree about what a period is.
 */
export function bucketSeries(
  rows: readonly Record<string, unknown>[],
  bucketKey: string,
  valueKey?: string,
): { labels: string[]; values: number[] } {
  const totals = new Map<string, number>()
  for (const row of rows) {
    const bucket = text(row[bucketKey])
    if (!bucket) continue
    const add = valueKey === undefined ? 1 : numOr0(row[valueKey])
    totals.set(bucket, (totals.get(bucket) ?? 0) + add)
  }
  const labels = [...totals.keys()].sort()
  return { labels, values: labels.map((l) => totals.get(l) ?? 0) }
}

/** The largest point of a series, for a chart's footnote. */
export function peakOf(series: {
  labels: string[]
  values: number[]
}): { label: string; value: number } | null {
  let best: { label: string; value: number } | null = null
  series.labels.forEach((label, i) => {
    const value = series.values[i] ?? 0
    if (!best || value > best.value) best = { label, value }
  })
  return best
}

/** A bucket timestamp as a short, interval-appropriate axis label. */
export function bucketLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** The wait bands, in the order the API generates its columns. */
export const BAND_KEYS = ['Lt5', 'Lt10', 'Lt20', 'Lt30', 'Lt60', 'Lt90', 'Lt120', 'Gt120'] as const
export const BAND_LABELS = ['1-5s', '6-10s', '11-20s', '21-30s', '31-60s', '61-90s', '91-120s', '>120s'] as const

/** Total each wait band across the rows, for the distribution chart. */
export function bandTotals(
  rows: readonly Record<string, unknown>[],
  prefix: string,
): number[] {
  return BAND_KEYS.map((key) => sumCol(rows, `${prefix}${key}`))
}
